/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api from "../services/api";

export default function AdminAIUsage() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/admin/ai-usage');
        setStats(res.data);
      } catch (err) {
        console.error("Failed to fetch AI usage stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <div className="min-h-screen bg-background text-on-background">
        <Header />
        <Sidebar />
        <main className="ml-0 md:ml-20 pt-24 pb-12 px-4 md:px-8">
          <div className="max-w-7xl mx-auto space-y-12 animate-pulse">
            <div className="h-20 bg-surface-container-low rounded-3xl w-1/3" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-32 bg-surface-container-low rounded-[2rem]" />
              ))}
            </div>
            <div className="h-96 bg-surface-container-low rounded-[3rem]" />
          </div>
        </main>
      </div>
    );
  }

  const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ec4899'];

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      <main className="ml-0 md:ml-20 pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      {/* Header Section */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center gap-2">
           <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
           <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">
             System Operations
           </span>
        </div>
        <h2 className="text-5xl font-black text-on-surface tracking-tighter leading-tight">
          AI Intelligence <span className="text-outline-variant/40">Usage.</span>
        </h2>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard 
          label="Total Tokens Consumed" 
          value={stats.totalTokens} 
          subtitle="Lifetime Token Spend"
          icon="memory"
          highlight
        />
        <MetricCard 
          label="Prompt Tokens" 
          value={stats.totalPromptTokens} 
          subtitle="Input Cost"
          icon="input"
        />
        <MetricCard 
          label="Candidate Tokens" 
          value={stats.totalCandidatesTokens} 
          subtitle="Generation Cost"
          icon="output"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        {/* Left Section: Time Series (8 Cols) */}
        <div className="xl:col-span-8 space-y-8">
          <div className="bg-surface-container-low p-10 rounded-[3rem] border border-outline-variant/10 shadow-sm space-y-8 relative overflow-hidden flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/5 text-primary flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-[18px]">
                  monitoring
                </span>
              </div>
              <div>
                <h3 className="text-xl font-black text-on-surface tracking-tight mb-2">30-Day Token Consumption</h3>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Platform AI Activity Trend</p>
              </div>
            </div>

            <div className="h-[300px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={Array.isArray(stats.chartData) ? stats.chartData : []}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.03)" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{fontSize: 10, fontWeight: 900, fill: '#64748b'}} 
                    dy={10} 
                  />
                  <YAxis hide />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000', 
                      border: 'none', 
                      borderRadius: '16px',
                      boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                      padding: '12px 16px' 
                    }}
                    itemStyle={{ color: '#fff', fontSize: '11px', fontWeight: 900 }}
                    labelStyle={{ color: '#64748b', fontSize: '10px', fontWeight: 900, marginBottom: '8px', textTransform: 'uppercase' }}
                  />
                  <Line type="monotone" dataKey="tokens" name="TOKENS" stroke="var(--color-primary)" strokeWidth={4} dot={{ r: 4, strokeWidth: 0, fill: 'var(--color-primary)' }} activeDot={{ r: 6, strokeWidth: 0 }} animationDuration={2000} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Recent Logs Table */}
          <div className="bg-surface-container-low p-10 rounded-[3rem] border border-outline-variant/10 shadow-sm relative overflow-hidden">
             <h3 className="text-xl font-black text-on-surface tracking-tight mb-6">Recent AI Operations</h3>
             <div className="overflow-x-auto">
               <table className="w-full text-left border-collapse">
                 <thead>
                   <tr className="border-b border-outline-variant/10">
                     <th className="pb-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Date</th>
                     <th className="pb-4 text-[10px] font-black uppercase tracking-widest text-slate-400">User</th>
                     <th className="pb-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Operation</th>
                     <th className="pb-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Tokens</th>
                   </tr>
                 </thead>
                 <tbody>
                   {Array.isArray(stats.rawLogs) ? stats.rawLogs.map((log: any) => (
                     <tr key={log.id} className="border-b border-outline-variant/5 hover:bg-surface-container-high transition-colors">
                       <td className="py-4 text-xs font-bold text-on-surface">{new Date(log.created_at || log.createdAt).toLocaleDateString()}</td>
                       <td className="py-4 text-xs font-bold text-on-surface">{log.user?.name || log.userId}</td>
                       <td className="py-4 text-xs font-bold text-primary">{log.serviceType}</td>
                       <td className="py-4 text-xs font-black text-on-surface text-right">{log.totalTokens.toLocaleString()}</td>
                     </tr>
                   )) : null}
                 </tbody>
               </table>
             </div>
          </div>
        </div>

        {/* Right Section: Service Dist & Top Users (4 Cols) */}
        <div className="xl:col-span-4 space-y-8 h-fit">
          <div className="bg-surface-container-low p-10 rounded-[3rem] border border-outline-variant/10 shadow-sm relative overflow-hidden">
             <h3 className="text-xl font-black text-on-surface tracking-tight mb-2">Process Distribution</h3>
             <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-8">Token Usage By Process</p>
             
             <div className="h-[240px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={Array.isArray(stats.byService) ? stats.byService : []}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      animationBegin={500}
                      animationDuration={1500}
                    >
                      {Array.isArray(stats.byService) ? stats.byService.map((_entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                      )) : null}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#000', 
                        border: 'none', 
                        borderRadius: '16px',
                        padding: '12px 16px',
                        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.3)'
                      }}
                      itemStyle={{ color: '#fff', fontSize: '11px', fontWeight: 900 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
             </div>
             
             <div className="space-y-3 mt-4">
               {Array.isArray(stats.byService) ? stats.byService.map((entry: any, index: number) => (
                 <div key={entry.name} className="flex justify-between items-center text-xs font-bold">
                   <div className="flex items-center gap-2">
                     <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                     <span className="text-on-surface-variant uppercase">{entry.name}</span>
                   </div>
                   <span className="text-on-surface">{entry.value.toLocaleString()}</span>
                 </div>
               )) : null}
             </div>
          </div>
          
          <div className="bg-surface-container-low p-8 rounded-[3rem] border border-outline-variant/10 shadow-sm relative overflow-hidden">
             <h3 className="text-xl font-black text-on-surface tracking-tight mb-2">Top Consumers</h3>
             <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-6">Users By Token Spend</p>
             
             <div className="space-y-4">
               {Array.isArray(stats.topUsers) ? stats.topUsers.map((user: any, index: number) => (
                 <div key={index} className="flex justify-between items-center p-4 bg-surface-container-highest/20 rounded-2xl">
                   <div className="flex items-center gap-3">
                     <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black">
                       {index + 1}
                     </div>
                     <span className="text-xs font-bold text-on-surface">{user.name}</span>
                   </div>
                   <span className="text-xs font-black text-primary">{user.tokens.toLocaleString()}</span>
                 </div>
               )) : null}
             </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</div>
  );
}

function MetricCard({ label, value, subtitle, icon, highlight }: any) {
  return (
    <div className={`p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm transition-all group hover:scale-[1.02] ${
      highlight ? "bg-primary text-surface" : "bg-surface-container-low"
    }`}>
      <div className="flex justify-between items-start mb-6">
        <span className={`material-symbols-outlined text-xl ${highlight ? "text-white/40" : "text-primary/40"}`}>
          {icon}
        </span>
        <div className={`w-1.5 h-1.5 rounded-full ${highlight ? "bg-white/40" : "bg-primary/20"}`} />
      </div>
      <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1 ${highlight ? "text-white/60" : "text-slate-500"}`}>
        {label}
      </p>
      <h4 className="text-4xl font-black tracking-tighter mb-2">{value?.toLocaleString()}</h4>
      <p className={`text-[10px] font-bold uppercase tracking-widest ${highlight ? "text-white/40" : "text-slate-400"}`}>
        {subtitle}
      </p>
    </div>
  );
}
